import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';

import {
    getCachedBillingData,
    getPartialBillingData,
    getPartialCachedBillingData,
} from './data/api';
import { logError } from './utils/sentry-logger';

import Dashboard from './components/Dashboard';
import Plans from './components/Plans';
import CheckoutWithStripe from './components/CheckoutWithStripe';
import Domain from './components/Domain';
import CustomDomain from './components/Domain/CustomDomain';
import UpdateCard from './components/UpdateCard';
import Support from './components/Support';
import Backups from './components/Backups';
import HeaderNav from './components/shared/HeaderNav';
import CustomSendingDomain from './components/Domain/CustomSendingDomain';
import Loading from './components/shared/Loading';
import { LAPSED_TRIAL_CHECKOUT_STATE_KEY, ROUTES } from './utils/constants';
import getForceUpgradeStatus, { FORCE_UPGRADE_STATUS } from './utils/get-force-upgrade-status';

import parentWindowDataChannel from './data/ParentWindowDataChannel';

let hasSentBillingAppReady = false;

// Upper bound on how long readiness may wait for a paint frame. Long enough
// that a visible load still signals on the paint-aligned rAF path, short
// enough to stay well inside Ghost Admin's load-monitor timeout.
const READINESS_TIMER_BACKSTOP_MS = 500;

/**
 * @typedef {import('./utils/get-force-upgrade-status').ForceUpgradeStatus} ForceUpgradeStatus
 * @typedef {object} ForceUpgradeRouteState
 * @property {ForceUpgradeStatus} status
 * @property {string|null} requiredRoute
 * @property {string} resolvedPath
 * @typedef {object} AppRoutesProps
 * @property {(title: string) => void} updateTitle
 * @property {import('history').History} history
 */

/**
 * Maps a Force upgrade status to its required route.
 * @param {ForceUpgradeStatus|undefined} forceUpgradeStatus
 * @param {string|null|undefined} checkoutRoute
 * @param {boolean} isLapsedTrialCheckout
 * @returns {string|null}
 */
function getRequiredForceUpgradeRoute(forceUpgradeStatus, checkoutRoute, isLapsedTrialCheckout) {
    if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.INCOMPLETE_OWNER) {
        return checkoutRoute || ROUTES.DASHBOARD;
    }

    if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER) {
        return isLapsedTrialCheckout ? null : ROUTES.PLANS;
    }

    if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.NON_OWNER) {
        return ROUTES.DASHBOARD;
    }

    return null;
}

export function resetBillingAppReadyForTests() {
    hasSentBillingAppReady = false;
}

/**
 * Creates a SHA-256 hash of the data to detect changes
 * @param {object} data - The data object to hash
 * @returns {Promise<string>} A hexadecimal string representation of the SHA-256 hash
 */
async function hashData(data) {
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

/**
 * Logs an error to Sentry and triggers a full page reload
 * @param {Error} error - The error to log
 * @param {object} context - Additional context for Sentry logging (e.g., tags)
 * @returns {void}
 */
function logErrorAndReload(error, context) {
    logError(error, context);
    window.location.reload();
}

/**
 * Main routing component for the Billing Management App
 * Handles route rendering, navigation, and synchronization with Ghost Admin via ParentWindowDataChannel
 * @param {AppRoutesProps} props - Component props
 * @returns {React.ReactNode} The rendered routing component
 */
function AppRoutes({ updateTitle, history }) {
    const [showNav, setShowNav] = useState(false);
    const [limitUpdateKey, setLimitUpdateKey] = useState('');
    const [forceUpgradeRouteState, setForceUpgradeRouteState] = useState(
        /** @type {ForceUpgradeRouteState|undefined} */ (undefined),
    );
    const currentPath = history.location.pathname;
    const isLapsedTrialCheckout = Boolean(
        currentPath.startsWith(`${ROUTES.CHECKOUT}/`) &&
        history.location.state?.[LAPSED_TRIAL_CHECKOUT_STATE_KEY] === true,
    );
    const currentForceUpgradeRouteState =
        forceUpgradeRouteState?.resolvedPath === currentPath ? forceUpgradeRouteState : undefined;
    const forceUpgradeStatus = currentForceUpgradeRouteState?.status;
    const requiredForceUpgradeRoute = currentForceUpgradeRouteState?.requiredRoute;
    const requiredForceUpgradeRouteRef = useRef(requiredForceUpgradeRoute);

    useEffect(() => {
        requiredForceUpgradeRouteRef.current = requiredForceUpgradeRoute;
    }, [requiredForceUpgradeRoute]);

    useEffect(() => {
        let isCancelled = false;

        getCachedBillingData()
            .then((billingData) => {
                if (!isCancelled) {
                    const status = getForceUpgradeStatus(billingData);
                    setForceUpgradeRouteState({
                        status,
                        requiredRoute: getRequiredForceUpgradeRoute(
                            status,
                            billingData?.checkoutRoute,
                            isLapsedTrialCheckout,
                        ),
                        resolvedPath: currentPath,
                    });
                }
            })
            .catch((error) => {
                logError(error, {
                    level: 'warning',
                    tags: { source: 'forceUpgradeRoute' },
                });
                if (!isCancelled) {
                    setForceUpgradeRouteState({
                        status: FORCE_UPGRADE_STATUS.NONE,
                        requiredRoute: null,
                        resolvedPath: currentPath,
                    });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [currentPath, isLapsedTrialCheckout]);

    /**
     * Handles toggling the navigation header visibility
     * @param {boolean} show - Whether to show or hide the navigation
     * @returns {void}
     */
    const handleShowNav = (show) => {
        setShowNav(show);
    };

    /**
     * Handles limit update requests from Ghost Admin via ParentWindowDataChannel
     * Fetches fresh limit data, compares it with cached data using hash comparison,
     * and triggers re-render if limits have changed. On error, triggers full page reload.
     * @returns {Promise<void>}
     */
    const handleLimitUpdateRequest = async () => {
        try {
            let beforeHash = limitUpdateKey;

            if (!beforeHash) {
                // if we don't have a hash yet, create one from the current data
                const currentLimits = await getPartialCachedBillingData('currentSiteLimits');
                if (!currentLimits) {
                    logErrorAndReload(new Error('No cached limits available during limit update'), {
                        tags: { source: 'handleLimitUpdateRequest' },
                    });
                    return;
                }
                beforeHash = await hashData(currentLimits);
            }

            const newLimits = await getPartialBillingData('currentSiteLimits');

            if (!newLimits) {
                logErrorAndReload(new Error('Failed to fetch updated site limits'), {
                    tags: { source: 'handleLimitUpdateRequest' },
                });
                return;
            }

            // Hash the new limits data
            const afterHash = await hashData(newLimits);

            // Only trigger re-render if the data actually changed
            if (beforeHash !== afterHash) {
                setLimitUpdateKey(afterHash);
            }
        } catch (error) {
            logErrorAndReload(error, {
                tags: { source: 'handleLimitUpdateRequest' },
            });
        }
    };

    parentWindowDataChannel.subscribe('limitUpdate', handleLimitUpdateRequest);

    useEffect(() => {
        let readinessFrame;
        let readinessTimeout;
        let isCancelled = false;

        const sendBillingAppReady = () => {
            if (isCancelled || hasSentBillingAppReady) {
                return;
            }

            hasSentBillingAppReady = true;
            parentWindowDataChannel.sendBillingAppReady({
                route: history.location.pathname,
                state: 'loading',
            });
        };

        /**
         * Handles route requests from Ghost Admin, enforcing any required Force upgrade route.
         * Acknowledges the required route when already there; otherwise navigates to the resolved route.
         * @param {string} route - The requested route path
         * @returns {void}
         */
        const handleRouteUpdate = (route) => {
            const requiredRoute = requiredForceUpgradeRouteRef.current;

            if (requiredRoute && route !== requiredRoute) {
                if (history.location.pathname === requiredRoute) {
                    parentWindowDataChannel.sendMessage({ route: requiredRoute });
                    return;
                }

                return history.push(requiredRoute);
            }

            return history.push(route);
        };

        const unlisten = history.listen((location) => {
            parentWindowDataChannel.sendMessage({
                route: location.pathname,
            });
        });

        parentWindowDataChannel.sendMessage({
            route: history.location.pathname,
        });

        parentWindowDataChannel.subscribe('routeUpdate', handleRouteUpdate);

        if (!hasSentBillingAppReady) {
            // The paint-aligned handoff never fires while the document isn't
            // rendered — Ghost Admin preloads this app in a display:none
            // iframe, where rAF callbacks are suspended — so a timer backstops
            // it. sendBillingAppReady is idempotent; whichever fires first wins.
            if (typeof window.requestAnimationFrame === 'function') {
                readinessFrame = window.requestAnimationFrame(sendBillingAppReady);
            }
            readinessTimeout = window.setTimeout(sendBillingAppReady, READINESS_TIMER_BACKSTOP_MS);
        }

        return () => {
            isCancelled = true;
            if (readinessFrame !== undefined) {
                window.cancelAnimationFrame(readinessFrame);
            }
            if (readinessTimeout !== undefined) {
                window.clearTimeout(readinessTimeout);
            }
            unlisten();
        };
    }, [history]);

    if (currentForceUpgradeRouteState === undefined) {
        return (
            <>
                <HeaderNav showNav={false} />
                <Loading />
            </>
        );
    }

    const isForceUpgradeNonOwner = forceUpgradeStatus === FORCE_UPGRADE_STATUS.NON_OWNER;
    const shouldRedirect =
        requiredForceUpgradeRoute !== null &&
        history.location.pathname !== requiredForceUpgradeRoute;

    return (
        <>
            <HeaderNav showNav={showNav && !isForceUpgradeNonOwner} />
            {shouldRedirect ? (
                <Redirect to={requiredForceUpgradeRoute} />
            ) : (
                <Switch key={limitUpdateKey}>
                    <Route exact path="/">
                        <Dashboard updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/billing">
                        <Dashboard updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/billing/plans">
                        <Plans updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/plans">
                        <Plans updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/update-card">
                        <UpdateCard updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/billing/update-card">
                        <UpdateCard updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/update-card/return">
                        <UpdateCard
                            returnToAdmin
                            updateTitle={updateTitle}
                            showNav={handleShowNav}
                        />
                    </Route>
                    <Route exact path="/billing/update-card/return">
                        <UpdateCard
                            returnToAdmin
                            updateTitle={updateTitle}
                            showNav={handleShowNav}
                        />
                    </Route>
                    <Route exact path="/support">
                        <Support updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/backups">
                        <Backups updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/domain">
                        <Domain updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/domain/custom">
                        <CustomDomain updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/domain/sending">
                        <CustomSendingDomain updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/checkout/:id">
                        <CheckoutWithStripe updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/billing/checkout/:id">
                        <CheckoutWithStripe updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/checkout">
                        <CheckoutWithStripe updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route exact path="/billing/checkout">
                        <CheckoutWithStripe updateTitle={updateTitle} showNav={handleShowNav} />
                    </Route>
                    <Route
                        path="*"
                        render={(props) => {
                            const url = props.match.url;
                            const regex = /\/\/+/g;
                            if (url.match(regex)) {
                                const cleanUrl = url.replace(regex, '/');
                                return <Redirect to={cleanUrl} />;
                            } else {
                                return <Dashboard updateTitle={updateTitle} />;
                            }
                        }}
                    />
                </Switch>
            )}
        </>
    );
}

export default withRouter(AppRoutes);

AppRoutes.propTypes = {
    updateTitle: PropTypes.func.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
        listen: PropTypes.func.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};
